import React, { createContext, useState, useContext, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';

import { format } from 'date-fns';
import { DB } from '~/services/firebase';
import ICashFlow from '~/interfaces/ICashFlow';
import getCurrencyFormatted from '~/utils/getCurrencyFormatted';

interface LoadLaunchsProps {
  initHour: string;
  endHour: string;
  initDate: string;
  endDate: string;
  company_id: string;
}

interface CashFlowContextData {
  launchs: ICashFlow[];
  loadLaunchs(payload: LoadLaunchsProps): Promise<void>;
  clearContext(): void;
  isInitialized: boolean;
}

const CashFlowContext = createContext<CashFlowContextData>(
  {} as CashFlowContextData,
);

export const CashFlowProvider: React.FC = ({ children }) => {
  const [launchs, setLaunchs] = useState<ICashFlow[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  const clearContext = useCallback(() => {
    setLaunchs([]);
  }, []);

  const loadLaunchs = useCallback(
    async ({
      initHour,
      endHour,
      initDate,
      endDate,
      company_id,
    }: LoadLaunchsProps) => {
      const [iHour, iMinute] = initHour.split(':');
      const [eHour, eMinute] = endHour.split(':');

      const initialDate = new Date(initDate);
      initialDate.setHours(parseInt(iHour));
      initialDate.setMinutes(parseInt(iMinute));
      initialDate.setSeconds(0);
      initialDate.setDate(initialDate.getDate() + 1);

      const finishDate = new Date(endDate);
      finishDate.setHours(parseInt(eHour));
      finishDate.setMinutes(parseInt(eMinute));
      finishDate.setSeconds(59);
      finishDate.setDate(finishDate.getDate() + 1);

      const q = query(
        collection(DB, 'cashflow'),
        where('company_id', '==', company_id),
        where('date', '>=', initialDate),
        where('date', '<=', finishDate),
      );

      const querySnapshot = await getDocs(q);

      const items: ICashFlow[] = [];

      querySnapshot.docs.forEach((item: any) => {
        const data = item.data() as ICashFlow;
        const date = new Date((data.date as any).seconds * 1000);

        const parseItem: ICashFlow = {
          ...data,
          uid: item.id,
          value_formatted: `${
            data.type === '0' ? '- ' : ''
          }${getCurrencyFormatted(data.value)}`,
          date_formatted: format(date, 'yyyy-MM-dd HH:mm'),
        };

        items.push(parseItem);
      });

      setLaunchs(items);
      setIsInitialized(true);
    },
    [],
  );

  return (
    <CashFlowContext.Provider
      value={{ launchs, loadLaunchs, clearContext, isInitialized }}
    >
      {children}
    </CashFlowContext.Provider>
  );
};

export function useCashFlow(): CashFlowContextData {
  const context = useContext(CashFlowContext);
  return context;
}
